// AGC: ProtectedRoute - Route guard component
// AGC: Protects routes that require authentication and/or specific roles
// AGC: Uses dynamic theme variables for consistent styling

import React, { ReactNode } from 'react'
import { useAuth } from '../../hooks'
import type { UserRole } from '../../types/user'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRole?: UserRole | UserRole[]
  fallback?: ReactNode
  loadingComponent?: ReactNode
}

/**
 * AGC: ProtectedRoute - Wraps components that need authentication
 * AGC: Optionally checks for specific user roles
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  fallback,
  loadingComponent,
}) => {
  const { isAuthenticated, isLoading, user, hasRole } = useAuth()

  // AGC: Show loading state
  if (isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>
    }
    
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0a3d33 0%, #0d4a3e 50%, #1a6b5a 100%)' }}
      >
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white drop-shadow-lg animate-pulse mb-4">DroidFarm</h1>
          <p className="text-white text-lg drop-shadow">Carregando...</p>
        </div>
      </div>
    )
  }

  // AGC: Not authenticated - show fallback or null
  if (!isAuthenticated || !user) {
    if (fallback) {
      return <>{fallback}</>
    }
    // AGC: Return null - parent should handle showing login
    return null
  }

  // AGC: Check role if required
  if (requiredRole) {
    const hasRequiredRole = hasRole(requiredRole)
    
    if (!hasRequiredRole) {
      // AGC: User doesn't have required role
      return (
        <div className="min-h-screen bg-gray-100 dark:bg-slate-900 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mx-auto mb-4 flex items-center justify-center">
              <span className="text-3xl">🚫</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
              Acesso Restrito
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Você não tem permissão para acessar esta página.
            </p>
            <div className="space-y-3">
              <a
                href="/"
                className="block w-full py-3 px-4 text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
                style={{ backgroundColor: 'var(--accent-primary)' }}
              >
                Voltar ao Início
              </a>
            </div>
          </div>
        </div>
      )
    }
  }

  // AGC: User is authenticated and has required role (if specified)
  return <>{children}</>
}

/**
 * AGC: LoadingScreen - Reusable loading component
 * AGC: Uses dynamic hero gradient from theme
 */
export const LoadingScreen: React.FC<{ message?: string }> = ({ message = 'Carregando...' }) => (
  <div 
    className="min-h-screen flex items-center justify-center"
    style={{ background: 'linear-gradient(135deg, #0a3d33 0%, #0d4a3e 50%, #1a6b5a 100%)' }}
  >
    <div className="text-center">
      <h1 className="text-3xl font-bold text-white drop-shadow-lg animate-pulse mb-4">DroidFarm</h1>
      <p className="text-white text-lg drop-shadow">{message}</p>
      <div className="mt-4 flex justify-center gap-1">
        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
      </div>
    </div>
  </div>
)

/**
 * AGC: AccessDenied - Reusable access denied component
 * AGC: Uses dynamic theme colors
 */
export const AccessDenied: React.FC<{ 
  message?: string
  requiredRole?: string
  currentRole?: string
}> = ({ 
  message = 'Você não tem permissão para acessar esta página.',
  requiredRole,
  currentRole,
}) => (
  <div className="min-h-screen bg-gray-100 dark:bg-slate-900 flex items-center justify-center p-4">
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md text-center">
      <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mx-auto mb-4 flex items-center justify-center">
        <span className="text-3xl">🚫</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
        Acesso Restrito
      </h1>
      <p className="text-gray-600 dark:text-gray-400 mb-4">{message}</p>
      
      {(requiredRole || currentRole) && (
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6 text-sm">
          {currentRole && (
            <p className="text-gray-600 dark:text-gray-400">
              Seu nível: <span className="font-medium text-gray-800 dark:text-white">{currentRole}</span>
            </p>
          )}
          {requiredRole && (
            <p className="text-gray-600 dark:text-gray-400">
              Nível necessário: <span className="font-medium" style={{ color: 'var(--accent-dark-text)' }}>{requiredRole}</span>
            </p>
          )}
        </div>
      )}
      
      <div className="space-y-3">
        <a
          href="/"
          className="block w-full py-3 px-4 text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
          style={{ backgroundColor: 'var(--accent-primary)' }}
        >
          Voltar ao Início
        </a>
      </div>
    </div>
  </div>
)

export default ProtectedRoute
